import { useNavigate } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { styles } from "../../styles/pages/JobTitle/JobTitleAddStyles";
import { Modal } from "../../components/ui/modal";
import { CheckCircle } from "lucide-react";

const JobTitleAdd = () => {
  const navigate = useNavigate();
  const [jobtitleName, setJobTitleName] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ job_title?: string }>({});
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    if (!jobtitleName.trim()) {
      setErrors({ job_title: "The Job Title field is required" });
      setSaving(false);
      return;
    }

    const formData = new FormData();
    formData.append("job_title", jobtitleName);

    const token = sessionStorage.getItem("access_token");
    if (!token) {
      setErrors({ job_title: "Authorization required. Please log in." });
      setSaving(false);
      navigate("/signin");
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/jobtitle/add`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === 200) {
        setSuccessModalOpen(true); // Show success modal
        setTimeout(() => {
          setSuccessModalOpen(false);
          navigate("/job-title-list"); // Redirect after modal
        }, 2000); // Delay for better UX
        setJobTitleName("");
        return;
      }
      if (response.data.status === 422 && response.data.message) {
        setErrors(
          Object.fromEntries(
            Object.entries(response.data.message).map(([key, value]) => [
              key,
              Array.isArray(value) ? value[0] : value,
            ])
          )
        );
        return;
      }

      if (response.data.status === 500) {
        setErrors({
          job_title: response.data.message || "Internal Server Error",
        });
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "An unexpected error occurred.";
      setErrors({ job_title: errorMessage });

      if (error.response?.status === 422 && error.response.data.errors) {
        setErrors(
          Object.fromEntries(
            Object.entries(error.response.data.errors).map(([key, value]) => [
              key,
              Array.isArray(value) ? value[0] : value,
            ])
          )
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setJobTitleName(value);
    if (!value.trim()) {
      setErrors({ job_title: "The Job Title field is required" });
    } else {
      setErrors({ job_title: undefined });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Add Job Title</h2>
      </div>

      <div className={styles.formContainer}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.inputContainer}>
            <div>
              <label className={styles.label}>
                Job Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={jobtitleName}
                onChange={handleInputChange}
                className={`${styles.input} ${
                  errors.job_title
                    ? styles.inputError
                    : "border-gray-300 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10"
                }`}
              />
              {errors.job_title && (
                <span className={styles.errorText}>{errors.job_title}</span>
              )}
            </div>
          </div>

          <div className={styles.buttonContainer}>
            <button
              type="submit"
              disabled={saving}
              className={styles.submitButton}
            >
              {saving ? "Saving..." : "Submit"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/job-title-list")}
              className={styles.cancelButton}
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Success Modal */}
        <Modal
          isOpen={isSuccessModalOpen}
          onClose={() => setSuccessModalOpen(false)}
          className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
        >
          {/* Close Button */}
          <button
            onClick={() => setSuccessModalOpen(false)}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          ></button>

          {/* Success Icon */}
          <div className="flex justify-center">
            <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
              <CheckCircle className="w-6 h-6" />
            </div>
          </div>

          {/* Modal Heading */}
          <h4 className="text-lg font-bold text-gray-800 dark:text-white mt-4">
            Job Title Added Successfully
          </h4>

          {/* Modal Text */}
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            The job title has been added to the system successfully.
          </p>

          {/* Button */}
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setSuccessModalOpen(false)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              OK
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default JobTitleAdd;
